import React, { useCallback, useState } from 'react';
import { History, Loader2, RotateCcw, Save, X } from 'lucide-react';
import {
  elencaVersioniVerbale, istantaneaVerbale, leggiVersioneVerbale, ripristinaVersioneVerbale, VersioneVerbale,
} from '../../services/verbaliService';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../ConfirmDialog';

interface VerbaleVersioniProps {
  eventId: string;
  /** Salva le modifiche in sospeso (prima di un'istantanea o di un ripristino): false ⇒ ci si ferma. */
  salvaSeServe: () => Promise<boolean>;
  onRipristinata: (contenuto: string, ultimaModifica: string | null) => void;
  disabled?: boolean;
}

const MOTIVI: Record<string, string> = {
  manuale: 'Salvata a mano',
  prima_di_impaginare: "Prima dell'impaginazione AI",
  prima_di_ripristinare: 'Prima di un ripristino',
  pubblicazione: 'Alla pubblicazione',
  prima_di_applicare: "Prima di una proposta dell'assistente",
  prima_di_generare: 'Prima della generazione dalla trascrizione',
};

const quando = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Versioni del verbale: elenco, anteprima in sola lettura e ripristino. */
const VerbaleVersioni: React.FC<VerbaleVersioniProps> = ({ eventId, salvaSeServe, onRipristinata, disabled }) => {
  const toast = useToast();
  const [aperta, setAperta] = useState(false);
  const [versioni, setVersioni] = useState<VersioneVerbale[]>([]);
  const [caricamento, setCaricamento] = useState(false);
  const [scelta, setScelta] = useState<string | null>(null);
  const [anteprima, setAnteprima] = useState<{ contenuto: string; caricamento: boolean }>({ contenuto: '', caricamento: false });
  const [conferma, setConferma] = useState(false);
  const [inCorso, setInCorso] = useState(false);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      setVersioni(await elencaVersioniVerbale(eventId));
    } catch {
      toast.error('Versioni non disponibili, riprova');
    } finally {
      setCaricamento(false);
    }
  }, [eventId, toast]);

  const apri = () => {
    setAperta(true);
    setScelta(null);
    setAnteprima({ contenuto: '', caricamento: false });
    carica();
  };

  const scegli = async (vid: string) => {
    setScelta(vid);
    setAnteprima({ contenuto: '', caricamento: true });
    try {
      const v = await leggiVersioneVerbale(eventId, vid);
      setAnteprima({ contenuto: v.contenuto || '', caricamento: false });
    } catch {
      setAnteprima({ contenuto: '', caricamento: false });
      toast.error('Versione non disponibile');
    }
  };

  const salvaVersione = async () => {
    setInCorso(true);
    try {
      if (!(await salvaSeServe())) return;
      const v = await istantaneaVerbale(eventId, 'manuale');
      toast.success(v ? 'Versione salvata' : 'Nessuna modifica dall\'ultima versione');
      await carica();
    } catch {
      toast.error('Versione non salvata, riprova');
    } finally {
      setInCorso(false);
    }
  };

  const ripristina = async () => {
    if (!scelta || disabled) return;
    setInCorso(true);
    try {
      if (!(await salvaSeServe())) return;
      const r = await ripristinaVersioneVerbale(eventId, scelta);
      onRipristinata(r.contenuto || '', r.ultimaModifica || null);
      toast.success('Versione ripristinata', 'Il testo di prima è tra le versioni.');
      setAperta(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Ripristino non riuscito, riprova');
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={apri}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
      >
        <History className="h-3.5 w-3.5" aria-hidden="true" />
        Versioni
      </button>

      {aperta && (
        <div
          className="fixed inset-0 z-[9000] flex items-center justify-center bg-slate-900/60 p-3 backdrop-blur-sm sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget && !inCorso) setAperta(false); }}
        >
          <div role="dialog" aria-modal="true" aria-label="Versioni del verbale" className="flex h-full max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
            <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3.5 dark:border-gray-700">
              <History className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
              <span className="text-base font-semibold text-slate-900 dark:text-white">Versioni del verbale</span>
              <button
                type="button"
                onClick={salvaVersione}
                disabled={inCorso}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
              >
                <Save className="h-3.5 w-3.5" aria-hidden="true" />
                Salva una versione ora
              </button>
              <button type="button" onClick={() => setAperta(false)} disabled={inCorso} aria-label="Chiudi" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-gray-700">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col md:flex-row">
              <ul className="max-h-48 flex-shrink-0 overflow-y-auto border-b border-slate-200 p-2 dark:border-gray-700 md:max-h-none md:w-72 md:border-b-0 md:border-r">
                {caricamento && (
                  <li className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-emerald-500" aria-hidden="true" /></li>
                )}
                {!caricamento && versioni.length === 0 && (
                  <li className="px-3 py-6 text-center text-xs text-slate-500 dark:text-gray-400">
                    Ancora nessuna versione: se ne salva una prima di ogni impaginazione, proposta applicata, ripristino e pubblicazione.
                  </li>
                )}
                {!caricamento && versioni.map((v) => (
                  <li key={v._id}>
                    <button
                      type="button"
                      onClick={() => scegli(v._id)}
                      aria-pressed={scelta === v._id}
                      className={`w-full rounded-lg px-3 py-2 text-left transition-colors ${
                        scelta === v._id ? 'bg-emerald-50 ring-1 ring-emerald-300 dark:bg-emerald-900/30 dark:ring-emerald-700' : 'hover:bg-slate-50 dark:hover:bg-gray-700/60'
                      }`}
                    >
                      <span className="block text-sm font-medium text-slate-800 dark:text-gray-100">{quando(v.createdAt)}</span>
                      <span className="block text-xs text-slate-500 dark:text-gray-400">
                        {MOTIVI[v.motivo] || v.motivo} · {v.caratteri.toLocaleString('it-IT')} caratteri
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4 dark:bg-gray-900/40 sm:p-6">
                  {!scelta ? (
                    <p className="mt-10 text-center text-sm text-slate-500 dark:text-gray-400">Scegli una versione per vederla.</p>
                  ) : anteprima.caricamento ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-emerald-500" aria-hidden="true" /></div>
                  ) : (
                    <article
                      className="testo-ricco mx-auto max-w-3xl rounded-xl bg-white px-6 py-6 font-serif text-slate-800 shadow-sm dark:bg-gray-800 dark:text-gray-100 sm:px-10"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(anteprima.contenuto) || '<p><em>Versione vuota</em></p>' }}
                    />
                  )}
                </div>
                <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setConferma(true)}
                    disabled={!scelta || anteprima.caricamento || inCorso || disabled}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
                  >
                    {inCorso ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    Ripristina
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={conferma}
        onClose={() => setConferma(false)}
        onConfirm={ripristina}
        title="Ripristinare questa versione?"
        message="Il foglio torna a questa versione. Il testo attuale si salva prima tra le versioni, quindi puoi sempre tornarci."
        confirmText="Ripristina"
        type="warning"
      />
    </>
  );
};

export default VerbaleVersioni;
